import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientId } from '../hooks/useClientId';
import { useValueSavings } from '../hooks/useValueSavings';
import { useIsSimulating } from '@/stores/simulationStore';
import { updateSavingsParams } from '../value.api';
import { useQueryClient } from '@tanstack/react-query';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { IncompleteCostBreakdown } from '../components/IncompleteCostBreakdown';
import { useIncompleteCost } from '../hooks/useIncompleteCost';
import { formatEur, formatNum } from '../format';

export function SavingsPage() {
  const { t } = useTranslation();
  const clientId = useClientId();
  const isSimulating = useIsSimulating();
  const queryClient = useQueryClient();

  const { data: savingsData, isLoading, isError, error, refetch } = useValueSavings(clientId);
  const { data: incompleteCostData } = useIncompleteCost(clientId);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  if (isError) {
    return <ApiErrorMessage error={error} onRetry={() => refetch()} />;
  }

  const savings = savingsData?.data;
  const params = savings?.parameters;

  const handleSaveParams = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!clientId || saving) return;
    setSaving(true);
    setSaveError(null);
    const fd = new FormData(e.currentTarget);

    const hourlyRate = Number(fd.get('hourlyRate'));
    const hoursPerProduct = Number(fd.get('hoursPerProduct'));
    const xxtractHoursPerProduct = Number(fd.get('xxtractHoursPerProduct'));
    const xxtractAnnualCost = Number(fd.get('xxtractAnnualCost'));

    // Client-side validation
    if (hourlyRate <= 0 || hoursPerProduct <= 0 || xxtractHoursPerProduct <= 0 || xxtractAnnualCost <= 0) {
      setSaveError(t('savings.validationPositive'));
      setSaving(false);
      return;
    }

    try {
      await updateSavingsParams(clientId, { hourlyRate, hoursPerProduct, xxtractHoursPerProduct, xxtractAnnualCost });
      await queryClient.invalidateQueries({ queryKey: ['value', 'savings'] });
      setIsEditing(false);
    } catch {
      setSaveError(t('savings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--foreground-heading)' }}>
          {t('savings.title')}
        </h1>
        <p className="mt-[3px]" style={{ fontSize: '0.8125rem', color: 'var(--foreground-muted)' }}>
          {t('savings.subtitle')}
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <div className="skeleton h-40 rounded-xl" />
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2"><div className="skeleton h-32 rounded-xl" /><div className="skeleton h-32 rounded-xl" /><div className="skeleton h-32 rounded-xl" /><div className="skeleton h-32 rounded-xl" /></div>
        </div>
      ) : !savings ? (
        <div className="flex flex-col items-center py-16 text-center" style={{ color: 'var(--foreground-muted)' }}>
          <span className="material-symbols-outlined mb-3" style={{ fontSize: '48px' }}>calculate</span>
          <p className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>{t('savings.empty')}</p>
        </div>
      ) : (
        <>
          {/* Hero card */}
          <div className="rounded-xl px-6 py-5" style={{ background: 'linear-gradient(135deg, var(--primary), var(--secondary))', color: 'white' }}>
            <p className="text-sm opacity-80">{t('savings.heroSubtitle')}</p>
            <p style={{ fontSize: '2.25rem', fontWeight: 700, lineHeight: 1.2 }}>{formatEur(savings.totalSavingEur)}</p>
            <p className="mt-1 text-sm opacity-80">{savings.quarter} &middot; {t('savings.heroCompare')}</p>
            <div className="mt-4 flex gap-6">
              <div><p className="text-2xl font-bold">{formatNum(Math.round(savings.hoursSaved))}</p><p className="text-xs opacity-80">{t('savings.hoursSaved')}</p></div>
              <div><p className="text-2xl font-bold">{savings.risksCovered.count}</p><p className="text-xs opacity-80">{t('savings.risksCovered')}</p></div>
            </div>
          </div>

          {/* Breakdown cards */}
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
            <BreakdownCard icon="schedule" iconColor="var(--secondary)" title={t('savings.timeSaving')}>
              <p className="text-sm" style={{ color: 'var(--foreground-muted)' }}>
                {formatNum(savings.totalProducts)} &times; {params?.hoursPerProduct ?? 12} {t('savings.hoursUnit')} = {formatNum(savings.timeSaving.hoursWithoutXxtract)} {t('savings.hoursUnit')}
              </p>
              <p className="text-sm" style={{ color: 'var(--foreground-muted)' }}>- XXtract: {formatNum(savings.timeSaving.hoursWithXxtract)} {t('savings.hoursUnit')}</p>
              <p className="mt-1 text-lg font-bold" style={{ color: 'var(--secondary)' }}>= {formatNum(Math.round(savings.timeSaving.hoursSavedPerQuarter))} {t('savings.hoursUnit')}/{t('savings.quarterUnit')}</p>
            </BreakdownCard>

            <BreakdownCard icon="savings" iconColor="var(--success-dark)" title={t('savings.costSaving')}>
              <p className="text-sm" style={{ color: 'var(--foreground-muted)' }}>{t('savings.selfDoing')}: {formatEur(savings.costSaving.costWithoutXxtract)}/{t('savings.yearUnit')}</p>
              <p className="text-sm" style={{ color: 'var(--foreground-muted)' }}>XXtract: {formatEur(savings.costSaving.costWithXxtract)}/{t('savings.yearUnit')}</p>
              <p className="mt-1 text-lg font-bold" style={{ color: 'var(--success-dark)' }}>= {formatEur(savings.costSaving.savingPerQuarter)}/{t('savings.quarterUnit')}</p>
            </BreakdownCard>

            <BreakdownCard icon="verified_user" iconColor="var(--secondary)" title={t('savings.riskAvoidance')}>
              {savings.risksCovered.items.map((riskKey, i) => (
                <div key={i} className="flex items-center gap-2 text-sm" style={{ color: 'var(--foreground-muted)' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '14px', color: 'var(--success-dark)' }}>check_circle</span>
                  {t(riskKey)}
                </div>
              ))}
            </BreakdownCard>

            <BreakdownCard icon="rocket_launch" iconColor="var(--primary)" title={t('savings.timeToMarket')}>
              <div className="flex items-center gap-3">
                <span className="rounded px-2 py-1 text-xs font-medium" style={{ backgroundColor: 'var(--destructive-light)', color: 'var(--destructive)' }}>
                  {t('savings.selfDoing')}: {savings.timeToMarket.withoutXxtractWeeks} {t('savings.weeksUnit')}
                </span>
                <span className="rounded px-2 py-1 text-xs font-medium" style={{ backgroundColor: 'var(--secondary-light)', color: 'var(--secondary)' }}>
                  XXtract: {savings.timeToMarket.withXxtractDays} {t('savings.daysUnit')}
                </span>
              </div>
            </BreakdownCard>
          </div>

          {/* Incompleteness cost breakdown */}
          {incompleteCostData?.data && (
            <IncompleteCostBreakdown data={incompleteCostData.data} />
          )}

          {/* Parameter editor */}
          <div className="rounded-xl px-5 py-4" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)', boxShadow: 'var(--card-shadow)' }}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{t('savings.parameters')}</p>
              {!isEditing && !isSimulating && (
                <button onClick={() => setIsEditing(true)} className="text-xs font-medium" style={{ color: 'var(--primary)' }}>{t('common.edit')}</button>
              )}
            </div>
            {saveError && <p className="mt-1 text-xs" style={{ color: 'var(--destructive)' }}>{saveError}</p>}
            {isEditing ? (
              <form onSubmit={handleSaveParams} className="mt-3 grid gap-3 grid-cols-2">
                <label htmlFor="hourlyRate" className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
                  {t('savings.paramHourlyRate')}
                  <input id="hourlyRate" name="hourlyRate" type="number" step="0.01" min="0.01" max="1000" required defaultValue={params?.hourlyRate ?? 45} className="mt-1 w-full rounded border px-2 py-1 text-sm" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }} />
                </label>
                <label htmlFor="hoursPerProduct" className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
                  {t('savings.paramHoursPerProduct')}
                  <input id="hoursPerProduct" name="hoursPerProduct" type="number" step="0.01" min="0.01" max="100" required defaultValue={params?.hoursPerProduct ?? 12} className="mt-1 w-full rounded border px-2 py-1 text-sm" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }} />
                </label>
                <label htmlFor="xxtractHoursPerProduct" className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
                  {t('savings.paramXxtractHours')}
                  <input id="xxtractHoursPerProduct" name="xxtractHoursPerProduct" type="number" step="0.01" min="0.01" max="50" required defaultValue={params?.xxtractHoursPerProduct ?? 2.25} className="mt-1 w-full rounded border px-2 py-1 text-sm" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }} />
                </label>
                <label htmlFor="xxtractAnnualCost" className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
                  {t('savings.paramAnnualCost')}
                  <input id="xxtractAnnualCost" name="xxtractAnnualCost" type="number" step="1" min="1" max="1000000" required defaultValue={params?.xxtractAnnualCost ?? 14000} className="mt-1 w-full rounded border px-2 py-1 text-sm" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }} />
                </label>
                <div className="col-span-2 flex gap-2 justify-end">
                  <button type="button" onClick={() => { setIsEditing(false); setSaveError(null); }} disabled={saving} className="rounded px-3 py-1.5 text-xs" style={{ color: 'var(--foreground-muted)' }}>{t('common.cancel')}</button>
                  <button type="submit" disabled={saving} className="rounded px-3 py-1.5 text-xs font-medium text-white" style={{ backgroundColor: 'var(--primary)' }}>{saving ? t('common.loading') : t('common.save')}</button>
                </div>
              </form>
            ) : params && (
              <div className="mt-2 grid gap-2 grid-cols-2 text-xs" style={{ color: 'var(--foreground-muted)' }}>
                <span>{t('savings.paramHourlyRate')}: {formatEur(params.hourlyRate)}</span>
                <span>{t('savings.paramHoursPerProduct')}: {params.hoursPerProduct}</span>
                <span>{t('savings.paramXxtractHours')}: {params.xxtractHoursPerProduct}</span>
                <span>{t('savings.paramAnnualCost')}: {formatEur(params.xxtractAnnualCost)}</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function BreakdownCard({ icon, iconColor, title, children }: { icon: string; iconColor: string; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl px-4 py-3.5" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)', boxShadow: 'var(--card-shadow)' }}>
      <div className="flex items-center gap-2 mb-2">
        <span className="material-symbols-outlined" style={{ fontSize: '18px', color: iconColor }}>{icon}</span>
        <p className="text-[0.8125rem] font-semibold" style={{ color: 'var(--foreground)' }}>{title}</p>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
